import { ReactNode } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import SearchableSelect from '../SearchableSelect/SearchableSelect';
import { UserOutlineIcon } from '../../icons/inputIcons';
import { IGrowerItem } from '../../../modules/API/Defaults/models/growers';

/** All the selector reads of a grower: a full IGrowerItem fits, so does a grower known only by id and name. */
export interface IGrowerOption {
    GrowerID: number;
    GrowerName: string;
}

interface Props<T extends IGrowerOption> {
    growers: T[];
    value: T | null;
    onSelect: (grower: T | null) => void;
    placeholder?: string;
    disabled?: boolean;
    readOnly?: boolean;
    allowClear?: boolean;
    clearLabel?: string;
    leadingIcon?: ReactNode | null;
    className?: string;
}

const GrowerSelector = <T extends IGrowerOption = IGrowerItem>({
    growers,
    value,
    onSelect,
    placeholder,
    disabled,
    readOnly,
    allowClear,
    clearLabel,
    leadingIcon,
    className,
}: Props<T>) => {
    const [t] = useTranslation('selectors');

    return (
        <SearchableSelect<T>
            items={growers}
            value={value}
            onSelect={onSelect}
            getKey={(g) => g.GrowerID}
            getLabel={(g) => (g.GrowerName ?? '').trim()}
            getSearchText={(g) => `${g.GrowerName ?? ''} ${g.GrowerID}`}
            renderItem={(g) => (
                <Row>
                    <GrowerName>{(g.GrowerName ?? '').trim()}</GrowerName>
                    <GrowerCode>{g.GrowerID}</GrowerCode>
                </Row>
            )}
            placeholder={placeholder ?? t('selectors_grower_placeholder')}
            disabled={disabled}
            readOnly={readOnly}
            allowClear={allowClear}
            clearLabel={clearLabel ?? t('selectors_clear')}
            leadingIcon={leadingIcon === undefined ? <UserOutlineIcon /> : leadingIcon}
            className={className}
            emptyLabel={t('selectors_grower_empty')}
        />
    );
};

export default GrowerSelector;

const Row = styled.span`
    display: inline-flex;
    align-items: baseline;
    gap: 0.5rem;
    min-width: 0;
`;

const GrowerName = styled.span`
    color: ${({ theme }) => theme.colors.ink};
    font-weight: 500;
`;

const GrowerCode = styled.span`
    color: ${({ theme }) => theme.colors.ink3};
    font-weight: 400;
`;
